


import {
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  reactive,
} from '@vue/composition-api';
import ReportModePane
  from '@/components/ExternalReports/EmergencyResponseTeam/components/ReportsView/ReportModePane/index.vue';
import MapMiscInfo from '@/components/ExternalReports/EmergencyResponseTeam/components/common/MapMiscInfo.vue';
import PhotoModal from '@/components/lib/PhotoModalWithEdit/index.vue';
import type {
  EmergencyResponseTeamMtxExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMtx';
import {
  EmergencyResponseTeamReportExt,
  EmergencyResponseTeamReportPhotoExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamReport';
import ManualDownloadLinks
  from '@/components/ExternalReports/EmergencyResponseTeam/components/common/ManualDownloadLinks/index.vue';
import usePaneControl from '@/components/ExternalReports/EmergencyResponseTeam/composables/usePaneControl';
import {
  useEmergencyResponseTeamReports,
} from '@/components/ExternalReports/EmergencyResponseTeam/composables/useReports';
import { useExtremeMap } from '@/components/ExternalReports/EmergencyResponseTeam/composables/useExtremeMap';
import { usePhotoModal } from '@/components/ExternalReports/EmergencyResponseTeam/composables/usePhotoModal';
import {
  REPORT_LIST_MAX_HEIGHT_RATE,
  REPORT_LIST_MIN_HEIGHT_RATE,
} from '@/components/ExternalReports/EmergencyResponseTeam/consts';
import { useRequestGuard } from '@/composables/useRequestGuard';
import {
  EmergencyResponseTeamSettings,
} from '@/models/apis/externalReports/emergencyResponseTeam/emergencyResponseTeamSettings';
import { fetchImageAsObjectURL, requestPhotos, revokePhotosObjectURLs } from '@/lib/imageHelper';
import type {
  EmergencyResponseTeamPhotoOrder,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamReport';
import { MAX_PENDING_PHOTOS_COUNT } from '@/consts/externalReports/hokkaidoAirport';

interface reportState {
  errorModalMsg: string;
  showErrorModal: boolean;
}
export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<EmergencyResponseTeamSettings>,
      required: true,
    },
  },
  setup() {
    const state = reactive<reportState>({
      errorModalMsg: '',
      showErrorModal: false,
    });
    const { paneState } = usePaneControl({
      paneStyleLimits: {
        listMinHeightRate: REPORT_LIST_MIN_HEIGHT_RATE,
        listMaxHeightRate: REPORT_LIST_MAX_HEIGHT_RATE,
      },
    });

    const {
      extremeMapRef,
      mapViewState,
      loadExtremeMapEssentials,
      forceResizeExtremeMap,
      showEmergencyResponseTeamReportLayer,
      removeEmergencyResponseTeamReportLayer,
      showEmergencyResponseTeamReportPhotoPopup,
      showEmergencyResponseTeamMtxPopup,
      hidePopup,
    } = useExtremeMap(paneState);

    const {
      reportState,
      searchReports,
      selectReport,
      deselectReport,
      getSelectedReport,
      getSelectedPhoto,
      getShowPhotos,
      deselectSelectedReportPhotos,
      resetSelectedPhotoSamePos,
      resetReportPhotosOrder,
      updateReportPhotosOrder,
      updateComment,
      deletePhoto,
      sendImageCandidate,
      updateReportPhotoSavedImages,
    } = useEmergencyResponseTeamReports();

    const { doRequest } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });

    const {
      photoModalState,
      showPhotoModal: showPhotoModalOrig,
      hidePhotoModal,
      refreshPhotos,
    } = usePhotoModal();
    const showPhotoModal = async(photoType: string, photos: EmergencyResponseTeamReportPhotoExt[]) => {
      await doRequest(async() => {
        await requestPhotos(photos);
      }, '画像の取得に失敗しました。');
      updateReportPhotoSavedImages(photos);
      showPhotoModalOrig(photoType, photos);
    };
    const showPhotoModalFromButton = async(photoType: string, photos: EmergencyResponseTeamReportPhotoExt[]) => {
      reportState.saveLocation = null;
      await showPhotoModal(photoType, photos);
    };
    const showPhotoModalFromMap = async(photoType: string, photos: EmergencyResponseTeamReportPhotoExt[]) => {
      if (photos.length > 0) {
        reportState.saveLocation = {lat: photos[0].lat, lon: photos[0].lon};
      }

      await showPhotoModal(photoType, photos);
    };

    const redrawEmergencyResponseTeamReportLayer = (fitToExtent = false) => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return;

      showEmergencyResponseTeamReportLayer(selectedReport, fitToExtent);
    };

    const onClickReport = async(selectedReport: EmergencyResponseTeamReportExt) => {
      hidePopup();
      // 選択中の報告書をクリックした場合、選択を解除する
      if (deselectReport(selectedReport.id)) {
        removeEmergencyResponseTeamReportLayer();
        return;
      }
      await doRequest(async() => {
        await selectReport(selectedReport.id);
      }, '報告書の取得に失敗しました。');
      redrawEmergencyResponseTeamReportLayer(true);
    };

    const onClickPhotoPin = async(photo: EmergencyResponseTeamReportPhotoExt) => {
      const isSelected = photo.isSelected;
      hidePopup();
      deselectSelectedReportPhotos();
      photo.isSelected = !isSelected;
      if (photo.isSelected) {
        // 画像を取得する
        if (!photo.savedImage) {
          await doRequest(async() => {
            photo.savedImage = await fetchImageAsObjectURL(photo.imagePath);
          }, '画像の取得に失敗しました。');
          if (!photo.savedImage) return;
        }

        showEmergencyResponseTeamReportPhotoPopup(photo);
      }
      redrawEmergencyResponseTeamReportLayer();
    };
    const onClickMtx = (mtx: EmergencyResponseTeamMtxExt) => {
      const isSelected = mtx.isSelected;
      deselectSelectedReportPhotos();
      hidePopup();
      mtx.isSelected = !isSelected;
      if (mtx.isSelected) {
        showEmergencyResponseTeamMtxPopup(mtx);
      }
      redrawEmergencyResponseTeamReportLayer();
    };
    const onAllDeselected = () => {
      hidePopup();
      deselectSelectedReportPhotos();
      redrawEmergencyResponseTeamReportLayer();
    };

    const paneResizeStopped = () => {
      forceResizeExtremeMap();
    };

    const addReportPhoto = async(file: File, photoType: string) => {
      await doRequest(async() => {
        await sendImageCandidate(file, photoType);
        await resetReportPhotosOrder();
      }, '写真の追加に失敗しました。');

      resetSelectedPhotoSamePos();
      refreshPhotoModel();
      refreshExtremeMapPhotos();
    };

    const updatePhotosOrder = async(photoId: number, newOrders: EmergencyResponseTeamPhotoOrder[]) => {
      await doRequest(async() => {
        await updateReportPhotosOrder(photoId, newOrders);
        await resetReportPhotosOrder();
      }, '写真の順序変更に失敗しました。');

      resetSelectedPhotoSamePos();
      refreshPhotoModel();
      refreshExtremeMapPhotos();
    };

    const deleteReportPhoto = async(photoId: number) => {
      await doRequest(async() => {
        await deletePhoto(photoId);
        await resetReportPhotosOrder();
      }, '写真の削除に失敗しました。');

      resetSelectedPhotoSamePos();
      refreshPhotoModel();
      refreshExtremeMapPhotos();
    };

    const updatePhotoComment = async(photoId: number, comment: string) => {
      await doRequest(async() => {
        await updateComment(photoId, comment);
      }, '写真のコメント更新に失敗しました。');

      resetSelectedPhotoSamePos();
      refreshPhotoModel();
      refreshExtremeMapPhotos();
    };

    const refreshPhotoModel = () => {
      const photos = getShowPhotos();
      if (!photos) return;

      refreshPhotos(photos);
    };

    const refreshExtremeMapPhotos = () => {
      redrawEmergencyResponseTeamReportLayer();
      if (!reportState.saveLocation) return;

      const photo = getSelectedPhoto();
      if (!photo) return;

      showEmergencyResponseTeamReportPhotoPopup(photo);
      hidePopup();
    };

    onMounted(async() => {
      await doRequest(async() => {
        await Promise.all([loadExtremeMapEssentials(), searchReports()]);
      }, '報告書の取得に失敗しました。');
    });

    onUnmounted(() => {
      revokePhotosObjectURLs(photoModalState.photos.map(p => p.savedImage));
    });

    return {
      state,
      mapViewState,
      paneState,
      photoModalState,
      // ref
      extremeMapRef,
      // consts
      MAX_PENDING_PHOTOS_COUNT,
      // methods
      deleteReportPhoto,
      hidePhotoModal,
      onClickReport,
      onClickPhotoPin,
      onClickMtx,
      onAllDeselected,
      paneResizeStopped,
      showPhotoModalFromMap,
      showPhotoModalFromButton,
      addReportPhoto,
      updatePhotosOrder,
      updatePhotoComment,
    };
  },
  components: {
    ReportModePane,
    PhotoModal,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
